import React, {FC, MouseEvent, useRef, useState, useEffect, useContext, useCallback} from 'react';
import Box from '@material-ui/core/Box';
import {withStyles} from '@material-ui/core';
import {useTheme} from '@material-ui/core/styles';
import {LegendList} from '@epic-visualizations/charts';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import {withLocalization} from 'epic-localization/src/context/LocalizationContext';

import LegendsWarningModal from './LegendsWarningModal';
import MoreLegendButton from './MoreLegendButton';
import LegendWithTooltip, {LegendWithTooltipProps} from './LegendWithTooltip';

import useWarningModal from '../useWarningModal';
import {useComparisonColor} from '@epic-visualizations/theme';
import {CODES} from '../../../../../../game-select/GameSelect';
import {LegendsLimitWarningContext} from './LegendsLimitWarningProvider';

import {LegendsProps} from './types';

const NO_DATA_CODE = 'epic.affiliate.fortnite_games.legends.no_data';
const MORE_CODE = 'epic.affiliate.fortnite_games.legends.more';

const StyledLegendContainer = withStyles(theme => ({
    root: {
        display: 'flex',
        alignItems: 'center',
        padding: theme.spacing(3, 0, 0, 0),

        '& > ul': {
            margin: theme.spacing(0, -1),
            padding: 0
        },

        '&:last-child': {
            margin: theme.spacing(0),
            padding: 0
        },

        [theme.breakpoints.up('fxs')]: {
            padding: theme.spacing(0),

            '& > ul': {
                margin: theme.spacing(0, -1.7)
            }
        }
    }
}))(Box);

const StyledLegendList = withStyles(theme => ({
    root: {
        width: '100%',

        '& svg': {
            fill: 'currentColor'
        },

        [theme.breakpoints.up('fsm')]: {
            height: 'auto',
            overflowY: 'visible'
        }
    }
}))(LegendList);

const Legends: FC<LegendsProps> = ({getMessage, ...props}: LegendsProps) => {
    const {
        limit: propsLimit,
        dataKeys,
        hiddenKeys,
        emptyDataKeys = [],
        selectedGames,
        onMouseEnter,
        onMouseLeave,
        onClick,
        hoveredKey,
        dataColors = {},
        isCompact = true,
        isTooltip,
        children
    } = props;
    const theme = useTheme();
    const getComparisonColor = useComparisonColor();
    const isFsmBreakpoint = useMediaQuery(theme.breakpoints.up('fsm'));
    const {open: warningOpen, onOpen, onCancel, onConfirm} = useWarningModal();
    const {confirmed, toggleConfirmed} = useContext(LegendsLimitWarningContext);

    const legendContainerRef = useRef(null);
    const [limit, setLimit] = useState(propsLimit);
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

    const open = Boolean(anchorEl);
    const isLastVisibleLegend = dataKeys.length - hiddenKeys.length === 1;
    const isLegendTruncated = !isFsmBreakpoint && dataKeys.length > limit && isCompact;

    const handleClose = (event: MouseEvent<HTMLElement>) => {
        setAnchorEl(anchorEl ? null : event.currentTarget);
    };

    const onClickProxy = useCallback(
        (key: string, isHidden = false) => {
            const visibleLegendsCount = dataKeys.length - hiddenKeys.length;

            if (!confirmed.current && visibleLegendsCount >= propsLimit && isHidden) {
                onOpen(() => {
                    onClick(key);
                    toggleConfirmed();
                });
            } else {
                onClick(key);
            }
        },
        [dataKeys, hiddenKeys]
    );

    const legends: LegendWithTooltipProps[] = selectedGames.map(({value, label, icon}, index) => {
        const key = String(value);
        const isHidden = hiddenKeys.includes(key);
        const isEmpty = emptyDataKeys.includes(key);
        const isLastVisibleItem = !isHidden && isLastVisibleLegend;

        return {
            label,
            value,
            index,
            onFocus: onMouseEnter,
            onBlur: onMouseLeave,
            onToggleVisibility: onClickProxy,
            legendProps: {
                isEmpty,
                isHidden,
                interactive: true,
                children: label,
                isHovered: hoveredKey === key,
                disabled: isLastVisibleItem || isEmpty,
                color: getComparisonColor(dataColors[key] || index),
                icon
            }
        };
    });

    const visibleLegends = legends.slice(0, limit);
    const hiddenLegends = legends.slice(limit, dataKeys.length);

    useEffect(() => {
        if (legendContainerRef.current) {
            const legends = Array.from(legendContainerRef.current.children || []);
            const {y: parentY} = legendContainerRef.current.getBoundingClientRect();
            let newLimit = 0;

            for (const legend of legends) {
                const {y} = legend.getBoundingClientRect();

                // If the legend is in the first line it should be visible
                if (!(y - parentY)) {
                    newLimit++;
                } else {
                    break;
                }
            }

            setLimit(isFsmBreakpoint ? propsLimit : newLimit || propsLimit);
        }
    }, [legendContainerRef.current, isFsmBreakpoint, isLegendTruncated]);

    return (
        <StyledLegendContainer>
            {/* @ts-ignore */}
            <StyledLegendList
                ref={legendContainerRef}
                style={{
                    height: isCompact ? 30 : 'auto',
                    overflowY: isCompact ? 'hidden' : 'visible'
                }}>
                {visibleLegends.map((props, index) => (
                    <LegendWithTooltip
                        key={props.label}
                        endAdornment={index === 1 && children}
                        isTooltip={isTooltip}
                        {...props}
                    />
                ))}
                {isFsmBreakpoint && dataKeys.length > propsLimit && (
                    <MoreLegendButton
                        chipLabel={`+${dataKeys.length - propsLimit} ${getMessage(MORE_CODE)}`}
                        title={getMessage(NO_DATA_CODE)}
                        onClose={handleClose}
                        open={open}
                        anchorEl={anchorEl}
                        resultsLabel={getMessage(CODES.searchResults)}
                        placeholder={getMessage(CODES.searchPlaceholder)}
                        options={hiddenLegends}
                    />
                )}
            </StyledLegendList>
            {isLegendTruncated && (
                <Box display="inline-block" width="fit-content">
                    <MoreLegendButton
                        chipLabel={`+${dataKeys.length - limit} ${getMessage(MORE_CODE)}`}
                        title={getMessage(NO_DATA_CODE)}
                        onClose={handleClose}
                        open={open}
                        anchorEl={anchorEl}
                        resultsLabel={getMessage(CODES.searchResults)}
                        placeholder={getMessage(CODES.searchPlaceholder)}
                        options={hiddenLegends}
                    />
                </Box>
            )}
            <LegendsWarningModal
                limit={propsLimit}
                open={warningOpen}
                onCancel={onCancel}
                onConfirm={onConfirm}
            />
        </StyledLegendContainer>
    );
};

export default withLocalization(Legends);
